import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { WEB_LOGIN_PATH } from "../../../routes/authPaths";

const VerifyEmail = () => {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const supabaseClient = useSupabaseClient();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token) {
          toast.error("Invalid verification link");
          setLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to verify email");
        }

        const userId = data.userId;

        if (userId) {
          const { error: updateError } = await supabaseClient.auth.admin.updateUserById(
            userId,
            {
              user_metadata: { email_verified: true }
            }
          );

          if (updateError) {
            try {
              const loginResult = await tryAutomaticLogin(userId);

              if (loginResult) {
                await supabaseClient.auth.updateUser({
                  data: { email_verified: true }
                });
              }
            } catch (err) {
              console.error("Error updating user metadata:", err);
            }
          }

          setVerified(true);
          toast.success(data.message);

          setLoginInProgress(true);
          const loginSuccess = await tryAutomaticLogin(userId);

          if (!loginSuccess) {
            setTimeout(() => {
              navigate(WEB_LOGIN_PATH);
            }, 2000);
          }
        }
      } catch (error) {
        console.error("Verification error:", error);
        toast.error(error.message || "Failed to verify email. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [location.search, navigate, supabaseClient]);

  const tryAutomaticLogin = async (verifiedUserId) => {
    try {
      const savedCredentials = localStorage.getItem('pendingVerification');

      if (!savedCredentials) {
        return false;
      }

      const credentials = JSON.parse(atob(savedCredentials));

      if (credentials.userId !== verifiedUserId) {
        return false;
      }

      const now = Date.now();
      const hasExpired = now - credentials.timestamp > 24 * 60 * 60 * 1000;

      if (hasExpired) {
        localStorage.removeItem('pendingVerification');
        return false;
      }

      const res = await supabaseClient.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      localStorage.removeItem('pendingVerification');

      if (res?.error) {
        throw res.error;
      }

      const emailConfirmed = res.data.user.user_metadata?.email_verified === true;

      if (!emailConfirmed) {
        await supabaseClient.auth.updateUser({
          data: { email_verified: true }
        });
      }

      setTimeout(() => {
        window.location.href = "/chat";
      }, 2000);

      return true;
    } catch (error) {
      console.error("Automatic login error:", error);
      localStorage.removeItem('pendingVerification');
      return false;
    }
  };

  return (
    <div className="email-verification-result">
      <div className="email-verification-result__container">
        {loading || loginInProgress ? (
          <>
            <h1 className="email-verification-result__title">
              {loading ? "Verifying your email..." : "Logging you in..."}
            </h1>
            <p className="email-verification-result__text">
              {loading
                ? "Please wait while we verify your email address."
                : "Your email is verified. Please wait while we log you in..."}
            </p>
          </>
        ) : verified ? (
          <>
            <h1 className="email-verification-result__title">Email Verified!</h1>
            <p className="email-verification-result__text">
              Your email has been successfully verified. You will be redirected to the application shortly.
            </p>
          </>
        ) : (
          <>
            <h1 className="email-verification-result__title">Verification Failed</h1>
            <p className="email-verification-result__text">
              We couldn't verify your email. The link may have expired or is invalid.
            </p>
            <button
              onClick={() => navigate(WEB_LOGIN_PATH)}
              className="email-verification-result__button"
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail; 